import {
  ISalesOrder,
  ISalesOrderInput,
  OrderType,
  PurchaseOrderLineStatus,
  SalesOrderLineStatus,
  SupplierReturnStatus,
} from '../graphql/schema/p4types';
import { StatusStyle } from '../components/connectedForm/HeaderStatus';

export type IIcon = {
  color: StatusStyle;
  type: string;
  tooltip?: string;
};

const awaitingIcon: IIcon = {
  type: 'error',
  color: StatusStyle.Danger,
};

const assignedAlternateIcon: IIcon = {
  type: 'blur_circular',
  color: StatusStyle.Danger,
};

const assignedOrAllocatedIcon: IIcon = {
  type: 'access_time',
  color: StatusStyle.Ready,
};

const shippedIcon: IIcon = {
  type: 'local_shipping',
  color: StatusStyle.Success,
};

const directShippedIcon: IIcon = {
  type: 'local_shipping',
  color: StatusStyle.Info,
};

const cancelIcon: IIcon = {
  type: 'cancel',
  color: StatusStyle.Danger,
};

export const getSalesOrderStatusIcon = (value: string | null): IIcon => {
  const icon: IIcon = { color: StatusStyle.Basic, type: 'info' };
  switch (value) {
    case SalesOrderLineStatus.AwaitingProduction:
      return { ...awaitingIcon, tooltip: 'There are unallocated kits/assemblies on this order/line' };
    case SalesOrderLineStatus.AwaitingAllocation:
      return { ...awaitingIcon, tooltip: 'There are unallocated non-kit products on this order/line' };
    case SalesOrderLineStatus.Reserved:
      return { ...awaitingIcon, tooltip: 'There is one or more reservations on this order/line' };
    case PurchaseOrderLineStatus.AwaitingDelivery:
      return { ...awaitingIcon, tooltip: 'This purchase order/line is yet to be (fully) received' };
    case SalesOrderLineStatus.AssignedShortage:
      return { ...assignedAlternateIcon, tooltip: 'This order/line is assigned to one or more purchase shortages' };
    case SalesOrderLineStatus.AssignedAssemblyShortage:
      return { ...assignedAlternateIcon, tooltip: 'This order/line is assigned to one or more assembly shortages' };
    case SalesOrderLineStatus.AssignedAssembly:
      return { ...assignedAlternateIcon, tooltip: 'This order/line is assigned to one or more assembly orders' };
    case SalesOrderLineStatus.InProduction:
      return { ...assignedOrAllocatedIcon, tooltip: 'This order/line has one or more allocated kits/assemblies' };
    case SalesOrderLineStatus.Allocated:
      return { ...assignedOrAllocatedIcon, tooltip: 'This order/line has one or more allocated non-kit products' };
    case SalesOrderLineStatus.AwaitingPick:
      return assignedOrAllocatedIcon;
    case SalesOrderLineStatus.Picked:
      return assignedOrAllocatedIcon;
    case SalesOrderLineStatus.Assigned:
      return { ...assignedOrAllocatedIcon, tooltip: 'This order/line is assigned to one or more purchase orders' };
    case SalesOrderLineStatus.Delivered:
      return shippedIcon;
    case SalesOrderLineStatus.Dispatched:
      return { ...shippedIcon, tooltip: 'This order/line has been partially or fully dispatched' };
    case SalesOrderLineStatus.OutForDelivery:
      return shippedIcon;
    case SalesOrderLineStatus.DirectDelivery:
      return {
        ...directShippedIcon,
        tooltip: 'Part of (or all of) this order will be delivered directly from the supplier.',
      };
    case PurchaseOrderLineStatus.Received:
      icon.type = 'local_shipping';
      icon.color = StatusStyle.Success;
      icon.tooltip = 'This order/line is partially or fully received';
      break;
    case PurchaseOrderLineStatus.ConsideredComplete:
      icon.type = 'check_circle';
      icon.color = StatusStyle.Success;
      icon.tooltip = 'This order/line is considered complete';
      break;
    case SalesOrderLineStatus.Invoiced:
      icon.type = 'check_circle';
      icon.color = StatusStyle.Success;
      icon.tooltip = 'This order/line is partially or fully invoiced';
      break;
    case SalesOrderLineStatus.Returned:
      icon.type = 'check_circle';
      icon.color = StatusStyle.Success;
      icon.tooltip = 'This order/line is partially or fully returned';
      break;
    case SupplierReturnStatus.Credited:
      icon.type = 'access_time';
      icon.color = StatusStyle.Warning;
      icon.tooltip = 'This return/line has been partially or fully credited';
      break;
    case SalesOrderLineStatus.FullyAllocated:
      icon.type = 'access_time';
      icon.color = StatusStyle.Success;
      icon.tooltip = 'This order/line has been fully allocated';
      break;
    case SalesOrderLineStatus.PriceApprovalRequired:
      icon.type = 'assignment_turned_in';
      icon.color = StatusStyle.Danger;
      icon.tooltip = 'This order/line requires manual price approval';
      break;
    case SalesOrderLineStatus.Pending:
      return { ...awaitingIcon, tooltip: 'This order/line has an Amazon Pending status' };
    case SalesOrderLineStatus.Cancelled:
      return { ...cancelIcon, tooltip: 'This order/line has an Amazon Cancelled status' };
  }
  return icon;
};

export function isQuote(order: ISalesOrderInput): boolean {
  if (order && order.orderType) {
    return order.orderType === OrderType.Quote;
  }
  return false;
}

export function isPendingPriceApproval(order: ISalesOrderInput | ISalesOrder): boolean {
  if (order && order.salesOrderStatus) {
    return order.salesOrderStatus.includes(SalesOrderLineStatus.PriceApprovalRequired);
  }
  return false;
}

export function isDocumentsDisabled(order: ISalesOrderInput | ISalesOrder): boolean {
  if (order && order.documentsDisabled) {
    return order.documentsDisabled;
  }
  return false;
}

export function isQuoteConversionAllowed(order: ISalesOrderInput): boolean {
  return isQuote(order) && !isPendingPriceApproval(order);
}

export function isOrder(order: ISalesOrderInput): boolean {
  if (order.archived) return false; //Don't allow actions (allocate stock, dispatch, cancel dispatch, etc) for archived orders.
  if (order && order.orderType) {
    return order.orderType === OrderType.Order;
  }
  return false;
}

export function isNonDraftOrder(order: ISalesOrderInput): boolean {
  if (order.draft) return false; //Actions beyond allocation are not supported for draft orders.
  return isOrder(order);
}

export function isDispatched(order: ISalesOrderInput): boolean {
  if (order.archived) return false;
  return !!(order && order.dispatchInstructions && order.dispatchInstructions.length > 0);
}

export const getSupplierReturnStatusIcon = (value: string): IIcon => {
  const icon: IIcon = { color: StatusStyle.Basic, type: 'info' };
  switch (value) {
    case SupplierReturnStatus.AwaitingReturn:
      icon.type = 'error';
      icon.color = StatusStyle.Danger;
      break;
    case SupplierReturnStatus.DebitNote:
    case SupplierReturnStatus.Credited:
      icon.type = 'access_time';
      icon.color = StatusStyle.Warning;
      break;
    case SupplierReturnStatus.Returned:
      icon.type = 'check_circle';
      icon.color = StatusStyle.Success;
      break;
  }
  return icon;
};

export const LUCKINS_CELL_COLOUR: string = '#428AFF';
